import { useMutation } from '@tanstack/vue-query'

import type {
  IChatBackgroundJobStartedEvent,
  IChatProcessingMetaEvent,
  IChatStreamEvent,
  IChatStreamInterruptedEvent,
} from '@/composables/useEventStream'
import { useEventStream } from '@/composables/useEventStream'
import { useMetricEvents } from '@/composables/useMetricEvents'
import { useChatStore } from '@/store/chat'
import { getChatDeletionInfo } from '@/store/chat/utils/chatExpiry'
import { useFileStore } from '@/store/file'
import { useModelStore } from '@/store/model'
import { chatSendEventProperties } from '@/utils/chatSendTracking'

import { normalizeStreamError } from '..'
import { bumpExpiringChatUpdatedAtInQueryCache } from './chatQueryCacheUtils'
import {
  createChatStreamActivityHandlers,
  createStreamState,
  handleInterruptedStream,
  handleMessageChunk,
  handleMessageCompleteChunk,
  handleNewTitleStreamEvent,
  handleReasoningChunk,
  handleStreamProgress,
  resetStreamMutationRefs,
  setupStreamPromiseHandling,
  stopThinkingAndResponding,
  streamMutationCallbacks,
} from './chatStreamHandlers'

import type { SendMessageData } from './chatQueryTypes'

/** Unix timestamp in seconds for optimistic user messages on the client. */
const getTimestamp = () => Math.floor(Date.now() / 1000)

/**
 * Factory for POST `/chats/:id/messages` with SSE: optimistic user row, then streamed assistant reply.
 *
 * @returns `send` mutation and reactive `streamState`
 */
export function useSendMessageMutation() {
  const { isStreaming, streamError, stopStream, streamState } =
    createStreamState()

  const send = useMutation<void, Error, SendMessageData>({
    mutationFn: (data: SendMessageData): Promise<void> => {
      return new Promise((resolve, reject) => {
        resetStreamMutationRefs(isStreaming, streamError, stopStream)

        const endpoint = `/chats/${data.chat_id}/messages`

        // `message_complete` is the only happy-path ending; anything else is a cut.
        let messageCompleted = false
        // Set when the server named the interruption, so EOF doesn't warn twice.
        let interruptionNotified = false

        /** Server closed the body mid-answer: no completion, no error, no user stop. */
        const wasCutShort = (wasAborted: boolean) =>
          !messageCompleted &&
          !interruptionNotified &&
          !wasAborted &&
          !streamError.value

        const model = data.model || data.gpt_object?.config?.model || ''
        // Pin the model this chat is running on, so it survives a reload even
        // when the reply names a model of its own (image generation, web
        // search) and the message history therefore can't reseed it. GPT chats
        // send no model of their own — theirs is pinned by the GPT.
        if (data.model) {
          useModelStore().rememberChatModel(data.chat_id, data.model)
        }
        // Update chat with user message
        useChatStore().setChatUserMessage(data.chat_id, {
          role: 'user',
          content: data.content,
          model,
          files: data.files,
          timestamp: getTimestamp(),
          doGenerateImage: data.generate_image,
        })
        useChatStore().setChatIsThinking(data.chat_id, true)
        useChatStore().setChatStreamPhase(data.chat_id, 'request_received')
        useChatStore().setChatIsResponding(data.chat_id, true)
        useMetricEvents().sendEvent('send_message', {
          conversation_id: data.chat_id,
          model_id: model,
          ...chatSendEventProperties(data),
        })

        const payload = {
          ...data,
        }
        // Clear file store
        useFileStore().clearUploadedFiles()

        const startedJobs: IChatBackgroundJobStartedEvent[] = []
        const eventStream = useEventStream(endpoint, {
          body: payload,
          method: 'POST',
          eventHandlers: {
            message_chunk: (chunkData: IChatStreamEvent) => {
              handleMessageChunk(chunkData)
            },
            reasoning_chunk: handleReasoningChunk,
            stream_progress: handleStreamProgress,
            ...createChatStreamActivityHandlers(startedJobs),
            processing_meta: (payload: IChatProcessingMetaEvent) => {
              useChatStore().appendProcessingMeta(payload)
            },
            new_title: handleNewTitleStreamEvent,
            message_complete: (chunkData: IChatStreamEvent) => {
              messageCompleted = true
              handleMessageCompleteChunk(chunkData, undefined, startedJobs)
            },
            stream_interrupted: (payload: IChatStreamInterruptedEvent) => {
              interruptionNotified = true
              handleInterruptedStream(
                payload?.chat_id ?? data.chat_id,
                payload?.message,
              )
            },
            error: (err) => {
              stopThinkingAndResponding(data.chat_id)
              const error = normalizeStreamError(err)
              ;(
                error as Error & { streamAffectedChatId?: string }
              ).streamAffectedChatId = data.chat_id
              streamError.value = error
              reject(error)
            },
          },
        })

        // Update stream state reactively
        isStreaming.value = true
        streamError.value = null
        stopStream.value = () => {
          eventStream.stop()
          isStreaming.value = false
          stopThinkingAndResponding(data.chat_id)
        }

        // Setup stream promise handling
        setupStreamPromiseHandling<void>(
          eventStream,
          isStreaming,
          streamError,
          resolve,
          reject,
          (wasAborted) => {
            const chatStore = useChatStore()
            if (wasCutShort(wasAborted)) {
              handleInterruptedStream(data.chat_id)
            } else {
              // Stream completed successfully; clear loader when stream resolves
              chatStore.flushAssistantStreamBuffer(data.chat_id)
              chatStore.setChatIsThinking(data.chat_id, false)
            }
            // Continuing an expiring chat keeps it active: bump `updated_at` so it leaves the
            // sidebar's "Expiring soon" section and hides the inline retention banner.
            const bumpedAt = getTimestamp()
            bumpExpiringChatUpdatedAtInQueryCache(data.chat_id, bumpedAt)
            const openChat = chatStore.getChatById(data.chat_id)
            if (openChat && getChatDeletionInfo(openChat)?.isExpiringSoon) {
              chatStore.setChat({ ...openChat, updated_at: bumpedAt })
            }
            resolve()
          },
        )
      })
    },
    ...streamMutationCallbacks(isStreaming, streamError, stopStream),
  })

  return {
    send,
    streamState,
  }
}
